import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import { track } from '@/lib/analytics';
import { Button, Card, Input } from '@/components';
import { OnboardingProgress } from '@/components/OnboardingProgress';
import { apiJson, ApiError } from '@/lib/api';
import type { HouseholdResponse } from '@/lib/types';

const TOTAL_STEPS = 11;

type ChildRow = { name: string; birthdate: string };

function ChildrenOnboardingPage() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    track('Children Names Screen');
  }, []);

  const [children, setChildren] = useState<ChildRow[]>([
    { name: '', birthdate: '' },
  ]);
  const [error, setError] = useState('');

  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
    enabled: isLoaded && !!isSignedIn,
  });

  const updateChild = (index: number, field: keyof ChildRow, value: string) => {
    setChildren((prev) =>
      prev.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const addChild = () =>
    setChildren((prev) => [...prev, { name: '', birthdate: '' }]);

  const removeChild = (index: number) => {
    if (children.length <= 1) return;
    setChildren((prev) => prev.filter((_, i) => i !== index));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const valid = children.filter((c) => c.name.trim());
      if (valid.length === 0) {
        throw new Error('Please enter at least one child name.');
      }

      let householdId = householdsQuery.data?.[0]?.id;

      if (!householdId) {
        const created = await apiJson<HouseholdResponse>(
          getToken,
          '/v1/households',
          { method: 'POST' }
        );
        householdId = created.id;
      }

      const payload = {
        beneficiaries: valid.map((c) => ({
          name: c.name.trim(),
          type: 'CHILD',
          ...(c.birthdate && { birthdate: c.birthdate }),
        })),
      };

      return apiJson(getToken, `/v1/households/${householdId}/beneficiaries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['households'] });
      navigate({ to: '/app/onboarding/building-plan' });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : "Couldn't save child names. Please try again.";
      setError(message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const valid = children.filter((c) => c.name.trim());
    if (valid.length === 0) {
      setError('Please enter at least one child name.');
      return;
    }
    setError('');
    mutation.mutate();
  };

  const nunitoFont =
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

  return (
    <div className='min-h-screen bg-sky-light'>
      <OnboardingProgress currentStep={10} totalSteps={TOTAL_STEPS} />
      <div className='max-w-md mx-auto px-6 py-12'>
        {/* Title */}
        <div className='text-center mb-8'>
          <h1
            className='text-3xl font-bold text-gray-900 mb-3'
            style={{ fontFamily: nunitoFont }}
          >
            Add your kids
          </h1>
          <p className='text-gray-500' style={{ fontFamily: nunitoFont }}>
            Add each child's name and an optional birthdate.
          </p>
        </div>

        {/* Form */}
        <Card>
          <form onSubmit={handleSubmit} className='space-y-4'>
            {children.map((child, index) => (
              <div key={index} className='flex items-start gap-2'>
                <div className='flex-1 space-y-2'>
                  <Input
                    label={`Child ${index + 1}`}
                    value={child.name}
                    onChange={(e) => updateChild(index, 'name', e.target.value)}
                    placeholder='Name'
                    disabled={mutation.isPending}
                    autoFocus={index === 0}
                  />
                  <div>
                    <label className='block text-sm font-semibold text-gray-600 mb-1'>
                      Birthdate (optional)
                    </label>
                    <input
                      type='date'
                      value={child.birthdate}
                      onChange={(e) =>
                        updateChild(index, 'birthdate', e.target.value)
                      }
                      max={new Date().toISOString().split('T')[0]}
                      disabled={mutation.isPending}
                      className='w-full px-4 py-2.5 rounded-2xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                    />
                  </div>
                </div>
                {children.length > 1 && (
                  <button
                    type='button'
                    onClick={() => removeChild(index)}
                    className='mt-7 p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600'
                    disabled={mutation.isPending}
                  >
                    <FiX size={16} />
                  </button>
                )}
              </div>
            ))}

            <button
              type='button'
              onClick={addChild}
              disabled={mutation.isPending}
              className='flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-hover transition-colors'
            >
              <FiPlus size={16} />
              Add another child
            </button>

            {error && (
              <p className='text-sm text-red-500 text-center'>{error}</p>
            )}

            <div className='pt-2'>
              <Button
                type='submit'
                fullWidth
                disabled={mutation.isPending}
                loading={mutation.isPending}
              >
                {mutation.isPending ? 'Saving…' : 'Continue'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/onboarding/children')({
  component: ChildrenOnboardingPage,
});
